import React, { useState } from 'react';
import styled from 'styled-components';
import { useAccount, useWalletClient } from 'wagmi';
// @ts-ignore - ZNS SDK types may not be available
import { ZNSConnect } from 'zns-sdk';

interface ZNSRegistrationModalProps {
  isVisible: boolean;
  onClose: () => void;
}

const ModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.$visible ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  padding: 32px;
  max-width: 500px;
  width: 100%;
  border: none;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 24px;
    max-width: 95%;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #9ca3af;
  font-size: 24px;
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.2s ease;

  &:hover {
    color: #ffffff;
    transform: scale(1.1);
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #9ca3af;
  margin: 0 0 24px 0;
`;

const InputContainer = styled.div`
  margin-bottom: 24px;
`;

const InputLabel = styled.label`
  display: block;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 8px;
`;

const DomainInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:focus-within {
    box-shadow: 0 0 0 2px rgba(168, 85, 247, 0.3);
  }
`;

const DomainInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  padding: 14px 16px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: #6b7280;
  }
`;

const DomainSuffix = styled.span`
  padding: 14px 16px;
  color: #a855f7;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
`;

const CheckButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #9333ea 0%, #7c3aed 100%);
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(147, 51, 234, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div<{ $type: 'success' | 'error' | 'info' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  background: transparent;
  border: none;
  
  ${props => props.$type === 'success' && `
    color: #4ade80;
  `}
  
  ${props => props.$type === 'error' && `
    color: #f87171;
  `}
  
  ${props => props.$type === 'info' && `
    color: #a855f7;
  `}
`;

const PriceDisplay = styled.div`
  background: transparent;
  border: none;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  text-align: center;
`;

const PriceLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #9ca3af;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PriceValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #a855f7;
`;

const RegisterButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  color: #ffffff;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(34, 197, 94, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 24px 0 0 0;
`;

const FeatureItem = styled.li`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 8px;
  padding-left: 24px;
  position: relative;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #a855f7;
    font-weight: 700;
  }
`;

const ExternalLinkButton = styled.a`
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  color: #a855f7;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  text-decoration: none;
  margin-top: 16px;

  &:hover {
    background: rgba(168, 85, 247, 0.1);
    transform: translateY(-2px);
  }
`;

// Mavrk referral address for ZNS commissions
// This is the wallet that receives referral rewards
const MAVRK_REFERRAL_ADDRESS = '0x94e19da85b4db72b95dfb71a69b12fd9b7a7dc55';

export const ZNSRegistrationModal: React.FC<ZNSRegistrationModalProps> = ({
  isVisible,
  onClose
}) => {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  
  const [domainName, setDomainName] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [price, setPrice] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  
  // Create ZNS SDK instance
  const zns = ZNSConnect();

  const handleCheckAvailability = async () => {
    if (!domainName.trim()) {
      setStatusMessage({ type: 'error', text: 'Please enter a domain name' });
      return;
    }

    if (!isConnected) {
      setStatusMessage({ type: 'error', text: 'Please connect your wallet first' });
      return;
    }

    setIsChecking(true);
    setStatusMessage(null);
    setIsAvailable(null);
    setPrice(null);

    try {
      console.log('🔍 [ZNS] Checking domain availability:', domainName);
      
      // Check if domain is already registered
      const isRegistered = await zns.checkDomain(`${domainName}.ink`);
      
      if (isRegistered) {
        setIsAvailable(false);
        setStatusMessage({ 
          type: 'error', 
          text: `${domainName}.ink is already registered. Try a different name.` 
        });
        console.log('❌ [ZNS] Domain already registered');
      } else {
        setIsAvailable(true);
        console.log('✅ [ZNS] Domain is available!');
        
        // Get the price for this domain
        console.log('💰 [ZNS] Fetching price...');
        const priceData = await zns.getPrice([domainName], 'ink');
        console.log('💰 [ZNS] Price data:', priceData);
        
        // Price is returned in wei, convert to ETH
        const priceInEth = (Number(priceData) / 1e18).toFixed(4);
        setPrice(priceInEth);
        
        setStatusMessage({ 
          type: 'success', 
          text: `${domainName}.ink is available! Ready to register.` 
        });
      }
      
    } catch (error: any) {
      console.error('❌ [ZNS] Error checking domain:', error);
      setStatusMessage({ 
        type: 'error', 
        text: error?.message || 'Failed to check domain availability. Please try again.' 
      });
      setIsAvailable(null);
    } finally {
      setIsChecking(false);
    }
  };

  const handleRegister = async () => {
    if (!walletClient || !address) {
      setStatusMessage({ type: 'error', text: 'Please connect your wallet to register' });
      return;
    }

    if (!isAvailable) {
      setStatusMessage({ type: 'error', text: 'Please check domain availability first' });
      return;
    }

    setIsRegistering(true);
    setStatusMessage({ type: 'info', text: 'Preparing registration transaction...' });

    try {
      console.log('🚀 [ZNS] Starting domain registration:', {
        domain: domainName,
        address,
        tld: 'ink'
      });

      // Register the domain with ZNS SDK
      // Note: Referral tracking is now confirmed to work with SDK registration!
      // Pass MAVRK_REFERRAL_ADDRESS to receive commission on this registration
      // @ts-ignore - Type mismatch between wagmi's walletClient and ZNS SDK's expected type
      const result = await zns.register(
        walletClient as any,
        [domainName],
        [address],
        'ink',
        MAVRK_REFERRAL_ADDRESS // Referral address for commission tracking
      );

      console.log('✅ [ZNS] Registration successful!', result);
      
      setStatusMessage({ 
        type: 'success', 
        text: `Success! ${domainName}.ink is now yours! Transaction may take a moment to confirm.` 
      });

      // Wait a bit to show success message, then close
      setTimeout(() => {
        onClose();
        // Optionally refresh the page or trigger a ZNS name refresh
        window.location.reload();
      }, 3000);

    } catch (error: any) {
      console.error('❌ [ZNS] Registration error:', error);
      
      let errorMessage = 'Failed to register domain. Please try again.';
      
      // Check for user cancellation/rejection
      if (
        error?.message?.toLowerCase().includes('user rejected') ||
        error?.message?.toLowerCase().includes('user denied') ||
        error?.message?.toLowerCase().includes('user cancelled') ||
        error?.name === 'UserRejectedRequestError'
      ) {
        errorMessage = 'User cancelled registration.';
      } else if (error?.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds for registration.';
      } else if (error?.shortMessage) {
        // Use shortMessage if available (cleaner than full message)
        errorMessage = error.shortMessage;
      }
      
      setStatusMessage({ 
        type: 'error', 
        text: errorMessage
      });
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <ModalOverlay $visible={isVisible} onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <ModalTitle>
          <img src="/zns_icon_purple.png" alt="ZNS" style={{ width: '28px', height: '28px' }} />
          Register Your .ink Domain
        </ModalTitle>
        <ModalSubtitle>
          Secure your Web3 identity with a ZNS domain name
        </ModalSubtitle>

        {!isConnected && (
          <StatusMessage $type="info">
            Please connect your wallet to register a domain
          </StatusMessage>
        )}

        <InputContainer>
          <InputLabel>Choose Your Domain Name</InputLabel>
          <DomainInputWrapper>
            <DomainInput
              type="text"
              placeholder="yourdomain"
              value={domainName}
              onChange={(e) => setDomainName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              onKeyPress={(e) => e.key === 'Enter' && handleCheckAvailability()}
            />
            <DomainSuffix>.ink</DomainSuffix>
          </DomainInputWrapper>
        </InputContainer>

        {statusMessage && (
          <StatusMessage $type={statusMessage.type}>
            {statusMessage.text}
          </StatusMessage>
        )}

        {price && isAvailable && (
          <PriceDisplay>
            <PriceLabel>Registration Price</PriceLabel>
            <PriceValue>{price} ETH</PriceValue>
          </PriceDisplay>
        )}

        <CheckButton
          onClick={handleCheckAvailability}
          disabled={!domainName.trim() || isChecking || !isConnected}
        >
          {isChecking ? 'Checking...' : 'Check Availability'}
        </CheckButton>

        {isAvailable && price && (
          <RegisterButton
            onClick={handleRegister}
            disabled={isRegistering}
          >
            {isRegistering ? 'Registering...' : `Register ${domainName}.ink`}
          </RegisterButton>
        )}

        <ExternalLinkButton
          href="https://zns.bio?ref=Y2Q0YzcyMm"
          target="_blank"
          rel="noopener noreferrer"
        >
          Browse All Available Domains →
        </ExternalLinkButton>

        <FeaturesList>
          <FeatureItem>Your Web3 identity on Ink</FeatureItem>
          <FeatureItem>Replace long wallet addresses with your name</FeatureItem>
          <FeatureItem>Earn 25% creator fees on Mavrk tokens</FeatureItem>
          <FeatureItem>Stand out in the creator economy</FeatureItem>
        </FeaturesList>
      </ModalContainer>
    </ModalOverlay>
  );
};

